import React, { useState, useRef, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card, Table, Badge, Alert } from 'react-bootstrap';
import { Link, useLocation, useHistory } from 'react-router-dom';
import ReactLoading from 'react-loading';
import Swal from 'sweetalert2';
import axios from 'axios';
import { BASE_URL } from "../../../constants/Endpoints"


const Censimenti = () => {
    const form = useRef(null);
    const location = useLocation();
    const history = useHistory();
    const [loading, setLoading] = useState(false);
    const [inputs, setInputs] = useState({
        partita_iva: '',
    });
    const [isPartitaIvaFromUrl, setIsPartitaIvaFromUrl] = useState(false);
    const [censimentiData, setCensimentiData] = useState(null);
    const [error, setError] = useState(null);
    const [contactId, setContactId] = useState(null);
    const [contactData, setContactData] = useState(null);

    const extractCensimentiError = (apiData) => {
        const censimenti = apiData?.censimenti;
        if (!censimenti || typeof censimenti !== 'object') return null;

        for (const companyResults of Object.values(censimenti)) {
            if (!Array.isArray(companyResults)) continue;

            for (const item of companyResults) {
                if (!item || typeof item !== 'object') continue;

                const providerKey = Object.keys(item).find((key) => !['time', 'success'].includes(key));
                if (!providerKey) continue;

                const providerData = item[providerKey];
                const maybeError = providerData?.error || providerData?.errore || providerData?.message;
                if (typeof maybeError === 'string' && maybeError.trim()) return maybeError;
            }
        }

        return null;
    };

    // Get contact_id from URL params if present and fetch contact details
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const contactIdParam = searchParams.get('contact_id');
        
        if (contactIdParam) {
            setContactId(contactIdParam);
            setIsPartitaIvaFromUrl(true);
            // Fetch contact details
            const fetchContact = async () => {
                try {
                    const response = await axios({
                        method: 'get',
                        url: `${BASE_URL}/api-v1/people/contact/${contactIdParam}/view`,
                        withCredentials: true
                    });
                    
                    if (response.data) {
                        setContactData(response.data);
                        setInputs({ partita_iva: response.data.codice_fiscale_datore || '' });
                    }
                } catch (err) {
                    console.error('Error fetching contact:', err);
                    setError('Errore nel recupero dei dati del contatto');
                }
            };
            
            fetchContact();
        }
    }, [location.search]);

    const handleChange = (event) => {
        const name = event.target.name;
        const value = event.target.value;
        setInputs(values => ({ ...values, [name]: value }));
    };

    const submitForm = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError(null);
        setCensimentiData(null);
        
        try {
            const response = await axios({
                method: 'get',
                url: `https://censimenti-csq.jazeeautomation.com/valutazione/api/CensimentoATC?codicefiscale=${inputs.partita_iva}`,
                headers: {
                    'x-api-key': process.env.REACT_APP_CENSIMENTI_API_KEY || ''
                }
            });

            if (response.data) {
                setCensimentiData(response.data);

                const apiError = extractCensimentiError(response.data);
                if (apiError) {
                    setError(apiError);
                    Swal.fire({
                    icon: 'error',
                    title: 'Errore',
                    text: apiError,
                });
                } else {
                    setError(null);
                }

                
                
                // Save to backend
                try {
                    await axios({
                        method: 'post',
                        url: `${BASE_URL}/api-v1/people/censimento/create`,
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        data: {
                            contact: contactId, // Link to contact if available
                            request: { codicefiscale: inputs.partita_iva },
                            response: response.data
                        },
                        withCredentials: true
                    });
                    console.log('Censimento saved to backend');
                } catch (saveError) {
                    console.error('Error saving censimento to backend:', saveError);
                    // Don't fail the main request if backend save fails
                }
            } else {
                setError('Nessun dato trovato per questa partita iva');
            }
        } catch (err) {
            console.error('Error fetching censimenti:', err);
            setError(err.response?.data?.message || 'Errore durante la ricerca dei censimenti');
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: 'Si è verificato un errore durante la ricerca',
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Container fluid className='smooth-transition'>
                <Row>
                    <Col lg="12">
                        <div className="d-flex flex-wrap align-items-center justify-content-between mb-4">
                            <div>
                                <h4 className="mb-3">Censimenti</h4>
                                <nav aria-label="breadcrumb">
                                    <ol className="breadcrumb p-0 mb-0">
                                        <li className="breadcrumb-item">
                                            <Link to="/">Dashboard</Link>
                                        </li>
                                        {contactId && (
                                            <li className="breadcrumb-item">
                                                <Link to={`/contact-view/${contactId}`}>
                                                    {contactData ? `${contactData.name} ${contactData.surname}` : 'Contatto'}
                                                </Link>
                                            </li>
                                        )}
                                        <li className="breadcrumb-item active" aria-current="page">Censimenti</li>
                                    </ol>
                                </nav>
                            </div>
                            <Button variant="primary" className="btn-sm d-flex align-items-center" onClick={() => history.goBack()}>
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" viewBox="0 0 20 20" fill="currentColor">
                                    <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                                </svg>
                                <span className="ml-2">Indietro</span>
                            </Button>
                        </div>
                    </Col>
                    <Col lg="12">
                        <Card>
                            <Card.Body className="smooth-transition">
                                <Form className="g-3" onSubmit={submitForm} ref={form}>
                                    <div className='row'>
                                        <div className="col-md-6 mb-3">
                                            <Form.Label htmlFor='partita_iva' className="form-label font-weight-bold text-muted text-uppercase">
                                                Partita Iva
                                            </Form.Label>
                                            <Form.Control 
                                                type='text' 
                                                className="form-control" 
                                                name='partita_iva' 
                                                placeholder=''
                                                maxLength="50"
                                                value={inputs["partita_iva"]}
                                                onChange={handleChange}
                                                disabled={isPartitaIvaFromUrl}
                                                required
                                            />
                                            {isPartitaIvaFromUrl && (
                                                <Form.Text className="text-muted">
                                                    Partita Iva precompilata dal contatto
                                                </Form.Text>
                                            )}
                                        </div>
                                    </div>

                                    {loading ? 
                                        <Button type="button" disabled>
                                            <ReactLoading
                                                className="mx-auto"
                                                type={"spin"}
                                                color={"#fefefe"}
                                                height={24}
                                                width={24}
                                            />
                                        </Button> 
                                        : 
                                        <Button type="submit">Search</Button>
                                    }
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>

                    {/* Error Display */}
                    {error && (
                        <Col lg="12" className="mt-3">
                            <Alert variant="danger">
                                {error}
                            </Alert>
                        </Col>
                    )}

                    {/* Results Display */}
                    {censimentiData && (
                        <>
                            {/* Company Information Header */}
                            <Col lg="12" className="mt-4">
                                <Card>
                                    <Card.Header>
                                        <h5 className="mb-0">Informazioni Aziendali</h5>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row>
                                            <Col md={6}>
                                                <p><strong>Ragione Sociale:</strong> {censimentiData.informazione_aziendale?.ragione_sociale}</p>
                                            </Col>
                                            <Col md={6}>
                                                <p><strong>Partita Iva:</strong> {censimentiData.informazione_aziendale?.codice_fiscale}</p>
                                            </Col>
                                        </Row>
                                    </Card.Body>
                                </Card>
                            </Col>

                            {/* Censimenti Results Tables (all companies) */}
                            {censimentiData.censimenti && Object.keys(censimentiData.censimenti).length > 0 ? (
                                (() => {
                                    const entries = Object.entries(censimentiData.censimenti).filter(([, arr]) => Array.isArray(arr));
                                    const hasAny = entries.some(([, arr]) => arr.length > 0);

                                    if (!hasAny) {
                                        return (
                                            <Col lg="12" className="mt-4">
                                                <Alert variant="info">
                                                    Nessun censimento trovato per questa partita iva.
                                                </Alert>
                                            </Col>
                                        );
                                    }

                                    return entries.map(([companyName, results]) => {
                                        if (!results || results.length === 0) return null;

                                        return (
                                            <Col lg="12" className="mt-4" key={companyName}>
                                                <Card>
                                                    <Card.Header>
                                                        <h5 className="mb-0">Risultati Censimenti - {companyName} ({results.length})</h5>
                                                    </Card.Header>
                                                    <Card.Body>
                                                        <Table responsive bordered hover>
                                                            <thead>
                                                                <tr>
                                                                    <th>Provider</th>
                                                                    <th>Tipo</th>
                                                                    <th>Indice</th>
                                                                    <th>Note</th>
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {results.map((item, index) => {
                                                                    const providerKey = Object.keys(item).find((key) => !['time', 'success'].includes(key));
                                                                    const providerData = providerKey ? item[providerKey] : null;

                                                                    if (!providerKey || !providerData) {
                                                                        return (
                                                                            <tr key={`${companyName}-${index}`}>
                                                                                <td colSpan={4}>Dato non valido</td>
                                                                            </tr>
                                                                        );
                                                                    }

                                                                    let indiceDisplay = providerData.indice_text ?? providerData.indice ?? 'N/A';
                                                                    if (providerData.upgrade && providerData.upgrade > 0) {
                                                                        const baseIndice = providerData.indice || 0;
                                                                        const finalIndice = baseIndice + providerData.upgrade;
                                                                        indiceDisplay = `${baseIndice} + ${providerData.upgrade} = ${finalIndice}`;
                                                                    }

                                                                    const rowClass = providerData.assumibile ? 'table-success' : '';

                                                                    return (
                                                                        <tr key={`${companyName}-${index}`} className={rowClass}>
                                                                            <td>{providerKey}</td>
                                                                            <td>{providerData.tipo_atc || '-'}</td>
                                                                            <td>{indiceDisplay}</td>
                                                                            <td>{providerData.nota || '-'}</td>
                                                                        </tr>
                                                                    );
                                                                })}
                                                            </tbody>
                                                        </Table>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                        );
                                    });
                                })()
                            ) : (
                                <Col lg="12" className="mt-4">
                                    <Alert variant="info">
                                        Nessun censimento trovato per questa partita iva.
                                    </Alert>
                                </Col>
                            )}
                        </>
                    )}
                </Row>
            </Container>
        </>
    );
};

export default Censimenti;